import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, TextInput, SelectInput } from "@/components/FormKit";
import { contactsApi, apiErrorMessage } from "@/api/client";
import { useAppData } from "@/context/AppDataContext";
import { toast } from "sonner";

const empty = {
  full_name: "", job_title: "", role_category: "", business_email: "",
  business_phone: "", preferred_contact_method: "any", contact_status: "active",
};

export function ContactFormDialog({ open, onOpenChange, orgId, existing, onSaved }) {
  const { enums } = useAppData();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) { setError(""); setForm(existing ? { ...empty, ...existing } : empty); }
  }, [open, existing]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) { setError("Full name is required"); return; }
    setSaving(true); setError("");
    const payload = {
      full_name: form.full_name,
      job_title: form.job_title || null,
      role_category: form.role_category || null,
      business_email: form.business_email || null,
      business_phone: form.business_phone || null,
      preferred_contact_method: form.preferred_contact_method,
      contact_status: form.contact_status,
    };
    try {
      const res = existing
        ? await contactsApi.update(existing.id, payload)
        : await contactsApi.create(orgId, payload);
      toast.success(existing ? "Contact updated" : "Contact added");
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="contact-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{existing ? "Edit contact" : "Add contact"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Full name">
            <TextInput testid="contact-name" value={form.full_name} onChange={set("full_name")} placeholder="Jane Fielding" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Job title"><TextInput testid="contact-job-title" value={form.job_title || ""} onChange={set("job_title")} /></Field>
            <Field label="Role">
              <SelectInput testid="contact-role" value={form.role_category || ""} onChange={set("role_category")}>
                <option value="">—</option>
                {(enums?.role_categories || []).map((r) => (
                  <option key={r} value={r}>{r.replace(/_/g, " ")}</option>
                ))}
              </SelectInput>
            </Field>
          </div>
          <Field label="Business email">
            <TextInput testid="contact-email" type="email" value={form.business_email || ""} onChange={set("business_email")} placeholder="jane@venue.co.uk" />
          </Field>
          <Field label="Business phone">
            <TextInput testid="contact-phone" value={form.business_phone || ""} onChange={set("business_phone")} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Preferred contact">
              <SelectInput testid="contact-preferred" value={form.preferred_contact_method} onChange={set("preferred_contact_method")}>
                {["any", "email", "phone"].map((m) => <option key={m} value={m}>{m}</option>)}
              </SelectInput>
            </Field>
            <Field label="Status">
              <SelectInput testid="contact-status" value={form.contact_status} onChange={set("contact_status")}>
                {["active", "inactive", "do_not_contact"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </SelectInput>
            </Field>
          </div>
          <p className="text-xs text-slate-400">Business contact details only. No personal data is collected in this phase.</p>
          {error && <p data-testid="contact-form-error" className="text-sm font-medium text-rose-600">{error}</p>}
          <button type="submit" disabled={saving} data-testid="contact-form-submit"
            className="min-h-[48px] w-full rounded-lg bg-teal-700 font-semibold text-white transition-colors hover:bg-teal-800 disabled:opacity-60">
            {saving ? "Saving…" : existing ? "Save changes" : "Add contact"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
